import i18n, {t} from 'i18next';

//ref: https://gitlab.com/mango-engineering/mango-applications/shared-modules/maknet-client-sdk/-/blob/main/src/shop/makroUser/types.ts?ref_type=heads#L87
export interface UserInfo {
  alcoholExpiryDate?: string;
}

import {CONTENT_TYPES, CURRENCY_CODES, PRODUCT_TYPE_1P, PRODUCT_TYPE_3P} from 'constants/recommendation';
import {AvailableLanguageTypes, Product, ProductSearchResult, ProductStatusType, SlabPriceTier} from 'types';
import {ProductUnavailableReasonCodes} from 'types/recommedation-types/recommendations';

import {safeToFixed} from './helper';
import {PRODUCT_ID_PREFIX} from '../constants/index';

export const roundUptoTwoDecimals = (value: number): number => {
  if (!value || isNaN(value)) {
    return 0;
  }

  return Math.round((value + Number.EPSILON) * 100) / 100;
};

export const isFeatureEnabled = (minVersion: string, currentVersion: string): boolean => {
  const normalizeVersion = (version: string) => version.match(/\d+/g)?.map(Number).join('.') || '0';

  return normalizeVersion(currentVersion).localeCompare(normalizeVersion(minVersion), undefined, {numeric: true}) >= 0;
};

//ref: https://gitlab.com/mango-engineering/mango-applications/shared-modules/maknet-client-sdk/-/blob/main/src/shop/product/util/statusUtils.ts?ref_type=heads#L38
export const isProductInStock = (product: Product) => {
  return (product === null || product === void 0 ? void 0 : product.totalInventory)
    ? product.totalInventory >= 1
    : false;
};
const isProductActive = (product: Product) => {
  let _a;
  return (
    (product === null || product === void 0 ? void 0 : product.status) !== undefined &&
    ((_a = product === null || product === void 0 ? void 0 : product.status) === null || _a === void 0
      ? void 0
      : _a.toLowerCase()) === ProductStatusType.ACTIVE.toLowerCase()
  );
};

//ref:https://gitlab.com/mango-engineering/mango-applications/shared-modules/maknet-client-sdk/-/blob/main/src/shop/product/util/statusUtils.ts?ref_type=heads#L116
const hasMetafields = (product: Product) => {
  let _a;
  const variant =
    (_a = product === null || product === void 0 ? void 0 : product.variants) === null || _a === void 0
      ? void 0
      : _a[0];
  return Boolean(variant === null || variant === void 0 ? void 0 : variant.metafields);
};

//ref: https://gitlab.com/mango-engineering/mango-applications/shared-modules/maknet-client-sdk/-/blob/main/src/shop/product/util/statusUtils.ts?ref_type=heads#L45
const isProductVariantActive = (variant: any) => {
  let _a;
  const activeMetafield =
    (_a = variant === null || variant === void 0 ? void 0 : variant.metafields) === null || _a === void 0
      ? void 0
      : _a.find((m: {key: string; value: string}) => m.key === 'status' && m.value === 'active');
  return Boolean(activeMetafield);
};

//ref: https://gitlab.com/mango-engineering/mango-applications/shared-modules/maknet-client-sdk/-/blob/main/src/shop/product/util/statusUtils.ts?ref_type=heads#L74
const MarketplaceShopType = {
  CONSIGNMENT: 'Consignment',
  DROPSHIP_SOD: 'Dropship_SOD',
  DROPSHIP_FBM: 'Dropship_FBM'
};

//ref: https://gitlab.com/mango-engineering/mango-applications/shared-modules/maknet-client-sdk/-/blob/main/src/shop/product/util/statusUtils.ts?ref_type=heads#L80
export const isDropShip = (marketplaceType: any) =>
  marketplaceType === MarketplaceShopType.DROPSHIP_FBM || marketplaceType === MarketplaceShopType.DROPSHIP_SOD;

//ref: https://gitlab.com/mango-engineering/mango-applications/shared-modules/maknet-client-sdk/-/blob/main/src/shop/product/util/statusUtils.ts?ref_type=heads#L56
const isProductAvailableForAllAvailableStores = (product: Product, storeCodes: string[]) => {
  let _a;
  if (product === null || product === void 0 ? void 0 : product.storeCode) {
    return storeCodes.includes(product?.storeCode || '');
  }
  const variant =
    (_a = product === null || product === void 0 ? void 0 : product.variants) === null || _a === void 0
      ? void 0
      : _a[0];
  if (variant === null || variant === void 0 ? void 0 : variant.outsideOfDeliveryZone) {
    return false;
  }
  return true;
};

//ref: https://gitlab.com/mango-engineering/mango-applications/shared-modules/maknet-client-sdk/-/blob/main/src/shop/product/util/statusUtils.ts?ref_type=heads#L84
const isProductAvailableForSelectedStore = (product: Product, currentStoreCode: string) => {
  let _a, _b, _c;
  const productStoreCode = product === null || product === void 0 ? void 0 : product.storeCode;
  if (productStoreCode) {
    if (!is1PMakroStore(productStoreCode)) {
      return true;
    }
    return productStoreCode === currentStoreCode;
  }
  if (
    isDropShip(
      (_a = product === null || product === void 0 ? void 0 : product.sellerDetails) === null || _a === void 0
        ? void 0
        : _a.marketplaceType
    )
  ) {
    return true;
  }
  return (
    ((_c =
      (_b = product === null || product === void 0 ? void 0 : product.variants) === null || _b === void 0
        ? void 0
        : _b[0]) === null || _c === void 0
      ? void 0
      : _c.storeCode) === currentStoreCode
  );
};

//ref: https://gitlab.com/mango-engineering/mango-applications/shared-modules/maknet-client-sdk/-/blob/main/src/shop/product/util/statusUtils.ts?ref_type=heads#L104
const isProductOutsideZone = (product: Product, storeCodes: string[] = [], is3pOwnfleetEnabled: boolean) => {
  let _a;
  if (is3pOwnfleetEnabled) {
    return !isProductAvailableForAllAvailableStores(product, storeCodes);
  }
  return !isProductAvailableForSelectedStore(product, (_a = storeCodes[0]) !== null && _a !== void 0 ? _a : '');
};

//ref: https://gitlab.com/mango-engineering/mango-applications/shared-modules/maknet-client-sdk/-/blob/main/src/shop/product/util/statusUtils.ts?ref_type=heads#L121
export const ProductAvailability = {
  available: 'available',
  outOfStock: 'outOfStock',
  notDeliverable: 'notDeliverable',
  unavailable: 'unavailable',
  notEligible: 'notEligible',
  moreThanMaxAllowed: 'moreThanMaxAllowed'
} as const;

//ref: https://gitlab.com/mango-engineering/mango-applications/shared-modules/maknet-client-sdk/-/blob/main/src/shop/product/util/statusUtils.ts?ref_type=heads#L132
const getProductAvailabilityWithFlag = (
  product: Product,
  storeCodes: string[],
  is3pOwnfleetEnabled: boolean,
  userInfo?: UserInfo
) => {
  let _a;
  const isMetafieldSupported = hasMetafields(product);

  if (!isMetafieldSupported && !isProductActive(product)) {
    return ProductAvailability.unavailable;
  }
  if (isMetafieldSupported && !isProductVariantActive(product.variants[0])) {
    return ProductAvailability.unavailable;
  }
  const expDate = userInfo?.alcoholExpiryDate ? new Date(userInfo.alcoholExpiryDate.toString()) : undefined;

  const today = new Date();
  let notExpiredLicense = false;
  if (expDate && expDate > today) {
    notExpiredLicense = true;
  }
  if (product?.isAlcohol && userInfo && !notExpiredLicense) {
    return ProductAvailability.notEligible;
  }
  const marketplaceType = (_a = product?.sellerDetails) === null || _a === void 0 ? void 0 : _a.marketplaceType;
  const is1P = !isDropShip(marketplaceType);
  const isSOD = marketplaceType === MarketplaceShopType.DROPSHIP_SOD;
  const isOutSideZone = isMetafieldSupported && isProductOutsideZone(product, storeCodes, is3pOwnfleetEnabled);

  if (isOutSideZone && is1P) {
    return ProductAvailability.unavailable;
  }
  if (isOutSideZone && isSOD) {
    return ProductAvailability.notDeliverable;
  }
  if (!isProductInStock(product)) {
    return ProductAvailability.outOfStock;
  }

  return ProductAvailability.available;
};

const getEngSingleUnitLabelFromPlural = (units: string) => {
  if (units.slice(-1) === 's') {
    return units.slice(0, -1);
  } else if (units.slice(-3) === '(s)') {
    return units.slice(0, -3);
  } else if (units.slice(-4) === '(es)') {
    return units.slice(0, -4);
  } else {
    return units;
  }
};

export const getProductIdWithoutPrefix = (id: string): string => {
  return id?.includes(PRODUCT_ID_PREFIX) ? id?.split(PRODUCT_ID_PREFIX).pop() || '' : id;
};

export const getUnitPerProduct = (
  productSize: string,
  isWeightedFresh?: boolean,
  isWeightedFreshV3Enabled?: boolean
) => {
  const unitFromProductSize = isWeightedFresh ? productSize.replace(/[0-9]./g, ' ') : productSize.replace(/[0-9]/g, '');
  if (isWeightedFreshV3Enabled) {
    return `/ ${i18n?.t('labels.Kg')}`;
  }
  if (i18n?.language === AvailableLanguageTypes.ENGLISH) {
    // unit from product size could be plural, so we need to convert it to singular
    const unit = getEngSingleUnitLabelFromPlural(unitFromProductSize);
    return `${i18n?.t('labels.per')}${unit}`;
  } else if (i18n?.language === AvailableLanguageTypes.THAI) {
    return `${i18n?.t('labels.per')}` + (isWeightedFresh ? unitFromProductSize : '');
  }

  return '';
};

export const getProductAvailability = (product: Product, storeCodes: string[], userInfo?: UserInfo) => {
  let status;
  if (product.unavailableReason?.code === ProductUnavailableReasonCodes.UNAVAILABLE_IN_RM3) {
    status = ProductAvailability.unavailable;
  } else if (product.unavailableReason?.code === ProductUnavailableReasonCodes.MORE_THAN_MAX_REQUIRED) {
    status = ProductAvailability.unavailable;
  } else {
    status = getProductAvailabilityWithFlag(product, storeCodes, true, userInfo);
  }
  return {
    status,
    isAvailable: status === ProductAvailability.available,
    isOutOfStock: status === ProductAvailability.outOfStock,
    isNotEligible: status === ProductAvailability.notEligible,
    isNotDeliverable: status === ProductAvailability.notDeliverable,
    isUnavailable: status === ProductAvailability.unavailable,
    isDisabled: status !== ProductAvailability.available
  };
};

export const getPricePerUnit = (productSize: string, displayPrice: number) => {
  const size_number = isNaN(parseFloat(productSize)) || parseFloat(productSize) <= 0 ? 1 : parseFloat(productSize);
  const unit_price_by_size = parseFloat(safeToFixed(displayPrice / size_number, 3));
  const unit_price = displayPrice && size_number > 0 ? unit_price_by_size : displayPrice;

  return unit_price;
};

export const getDiscountPercentage = (price = 0, origin: number): string | null => {
  if (!origin) {
    return null;
  }
  const discountPercentage = Math.floor(((origin - price) / origin) * 100);
  if (discountPercentage < 1) {
    return null;
  }
  return `${discountPercentage}`;
};

export const getDiscountDetailFromSlabTiers = (tiers: SlabPriceTier[], productCount: number, displayPrice: number) => {
  let startCount = 0;
  let endCount = 0;
  let priceValue = displayPrice;
  if (tiers.length) {
    if (productCount === 0) {
      if (tiers.length === 1) {
        startCount = tiers[0].quantity;
        endCount = 0;
        priceValue = tiers[0].priceInVat;
      } else {
        startCount = tiers[0].quantity;
        endCount = tiers[1].quantity - 1;
        priceValue = tiers[0].priceInVat;
      }
    } else {
      tiers.some((tier, index, arr) => {
        if (productCount < tier.quantity && index === 0) {
          startCount = 1;
          endCount = tier.quantity - 1;
          priceValue = displayPrice;
          return true;
        }
        if (productCount < tier.quantity) {
          startCount = arr[index - 1].quantity;
          endCount = tier.quantity - 1;
          priceValue = arr[index - 1].priceInVat;
          return true;
        }
        if (productCount >= tier.quantity && index === arr.length - 1) {
          startCount = tier.quantity;
          endCount = 0;
          priceValue = tier.priceInVat;
          return true;
        }
      });
    }
  }

  return {
    priceValue,
    startCount,
    endCount
  };
};

export function getSlabPriceValue(slabPriceTiers: SlabPriceTier[] = [], productCount = 0, productDisplayPrice = 0) {
  const isSlabPrice = (slabPriceTiers?.length ?? 0) > 0;
  const dynamicDiscount = getDiscountDetailFromSlabTiers(slabPriceTiers, productCount, productDisplayPrice);
  const priceValue = isSlabPrice ? dynamicDiscount.priceValue : productDisplayPrice;
  return {priceValue, dynamicDiscount};
}

export const getSlabPriceInfoText = (
  dynamicDiscount: {priceValue?: number; startCount: any; endCount: any},
  showKg = false
) => {
  let infoText = '';
  if (dynamicDiscount.startCount && !dynamicDiscount.endCount) {
    infoText = `${t('wholesale.buy')} ${dynamicDiscount.startCount}${
      showKg ? `+ ${t('labels.kg')}` : t('wholesale.plusUnits')
    }`;
  }
  if (dynamicDiscount.startCount && dynamicDiscount.endCount) {
    infoText = `${t('wholesale.buy')} ${dynamicDiscount.startCount} - ${
      dynamicDiscount.endCount
    } ${showKg ? t('labels.kg') : t('wholesale.units')}`;
  }
  return infoText;
};

export const is1PMakroStore = (storeCode: string | undefined) => storeCode && !storeCode.includes('3P');

export const getProductStoreCode = (product: Product | ProductSearchResult): string | undefined => {
  const storeCode = product?.storeCode || (product?.variants && product?.variants[0]?.storeCode);
  return storeCode;
};

export const removeProductIdPrefix = (productID: string) => {
  const removetPrefix = productID.replace(PRODUCT_ID_PREFIX, '');
  return removetPrefix || null;
};

export const productAnalytics = (product: Product | ProductSearchResult) => {
  const storeCode = getProductStoreCode(product);
  const is1Pproduct = is1PMakroStore(storeCode);
  return {
    productId: removeProductIdPrefix(product.id),
    sku:
      (product as Product)?.variants?.[0]?.sku !== undefined
        ? ((product as Product)?.variants?.[0]?.sku ?? '')
        : (product?.sku ?? ''),
    skuCode: product.skuCode || '',
    productType: is1Pproduct ? PRODUCT_TYPE_1P : PRODUCT_TYPE_3P,
    makroId: product?.makroId ? product.makroId : product.sku,
    variantId: product?.variants?.[0]?.id ?? ''
  };
};

export const marketingAnalytics = (product: Product, quantity?: number) => {
  return {
    value: product?.displayPrice,
    content_type: 'product',
    currency: 'THB',
    contents: [
      {
        ...getProductIdQty(product, quantity)
      }
    ]
  };
};

export const appsFlyerMarketingAnalytics = (product: Product, quantity = 1) => {
  return {
    value: product?.displayPrice,
    af_price: product?.displayPrice,
    af_content_type: CONTENT_TYPES.PRODUCT,
    af_currency: CURRENCY_CODES.THB,
    af_quantity: product?.quantity || quantity,
    af_content: JSON.stringify([
      {
        ...getProductIdQty(product, quantity)
      }
    ])
  };
};

export const getProductIdQty = (product: Product, basketQuantity?: number) => {
  return {
    id: `${product?.makroId ? product?.makroId : product?.sku}-${getProductIdWithoutPrefix(product?.id)}` || '',
    quantity: basketQuantity && basketQuantity > 0 ? basketQuantity : product?.quantity || 1
  };
};
